import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLocationCtx } from "@/context/LocationContext";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, MapPin } from "lucide-react";

export default function LocationSheet({ open, onClose }) {
  const { loc, setLoc } = useLocationCtx();
  const [locations, setLocations] = useState({});
  const [selState, setSelState] = useState(loc.state);

  useEffect(() => {
    if (open) api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
  }, [open]);

  const states = Object.keys(locations);
  const cities = selState ? locations[selState] || [] : [];

  const apply = (state, city) => {
    setLoc({ state, city });
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[60] bg-black/40"
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 32, stiffness: 320 }}
            className="fixed bottom-0 left-1/2 z-[70] w-full max-w-md -translate-x-1/2 rounded-t-3xl bg-white p-5 pb-8"
            data-testid="location-sheet"
          >
            <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-black/10" />
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-display text-xl font-bold">Elige tu ubicación</h3>
              <button onClick={onClose} data-testid="location-sheet-close" className="rounded-full bg-[#F3F0E6] p-2">
                <X size={18} />
              </button>
            </div>

            <button
              onClick={() => apply("", "")}
              data-testid="location-all"
              className="mb-4 flex w-full items-center gap-2 rounded-xl bg-[#F3F0E6] px-4 py-3 text-left text-sm font-bold"
            >
              <MapPin size={16} className="text-[#E54D2E]" />
              Todas las ubicaciones
            </button>

            <p className="mb-2 text-xs font-bold uppercase tracking-widest text-[#5C5C5C]">Estado</p>
            <div className="no-scrollbar mb-4 flex max-h-32 flex-col gap-1 overflow-y-auto">
              {states.length === 0 && <p className="text-sm text-[#5C5C5C]">Sin datos aún.</p>}
              {states.map((s) => (
                <button
                  key={s}
                  data-testid={`state-option-${s}`}
                  onClick={() => setSelState(s)}
                  className={`flex items-center justify-between rounded-lg px-3 py-2 text-left text-sm font-semibold ${
                    selState === s ? "bg-[#1A1A1A] text-white" : "hover:bg-[#F3F0E6]"
                  }`}
                >
                  {s}
                  {selState === s && <Check size={16} />}
                </button>
              ))}
            </div>

            {selState && (
              <>
                <p className="mb-2 text-xs font-bold uppercase tracking-widest text-[#5C5C5C]">Ciudad</p>
                <div className="no-scrollbar flex max-h-40 flex-col gap-1 overflow-y-auto">
                  <button
                    onClick={() => apply(selState, "")}
                    className="rounded-lg px-3 py-2 text-left text-sm font-semibold hover:bg-[#F3F0E6]"
                  >
                    Todo {selState}
                  </button>
                  {cities.map((c) => (
                    <button
                      key={c}
                      data-testid={`city-option-${c}`}
                      onClick={() => apply(selState, c)}
                      className="rounded-lg px-3 py-2 text-left text-sm font-semibold hover:bg-[#F3F0E6]"
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
